import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { AUDIENCE_NAV } from '@/data/audiences';

/**
 * "SOLUTIONS" DROPDOWN — the compact form of the four-audience navigation.
 *
 * WHY A SECOND FORM AT ALL:
 * The flat eight-item bar (Find Work · Homeowners · Commercial · Contractors ·
 * Subcontractors · How It Works · Pricing · Contact) measures roughly 810px
 * with padding. Add the logo (~150px) and the right-hand cluster (language
 * toggle + Log In + Sign Up ≈ 220px, or Profile + Dashboard + Log out ≈ 300px
 * when signed in) and it only fits inside the 1216px content box on xl screens
 * for signed-out visitors. Rather than let the bar wrap or clip between 768px
 * and 1280px, that range renders this grouped menu instead — same four
 * destinations, same order, no overflow.
 *
 * CRAWLABILITY:
 * This menu is never the only copy of the links. Header.tsx always renders the
 * flat list in the DOM (CSS-hidden below xl), the mobile sheet renders them
 * again, and the footer carries all four site-wide — so /homeowners,
 * /commercial, /general-contractors and /subcontractors are linked from every
 * page regardless of viewport and regardless of whether this menu is opened.
 *
 * ACCESSIBILITY:
 * Real <button> with aria-expanded/aria-haspopup, Escape closes it and returns
 * focus to the trigger, an outside click closes it, and the panel is a <ul> of
 * real anchors (not div+onClick), so keyboard and screen-reader users get the
 * same four options in the same order.
 */
const SolutionsMenu: React.FC = () => {
  const { t, path } = useI18n();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();

  // Hrefs are localised once here, so the active check works identically on
  // /en/commercial and /es/comercial.
  const items = AUDIENCE_NAV.map((a) => ({ ...a, href: path(a.to) }));
  const isCurrent = (href: string) => pathname === href || pathname === `${href}/`;
  const onAudiencePage = items.some((i) => isCurrent(i.href));

  // Close on navigation, so the panel never survives a route change.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        btnRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        className={`flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
          open || onAudiencePage
            ? 'bg-slate-100 text-slate-900'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
        }`}
      >
        {t('nav.solutions')}
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-2 w-[22rem] rounded-2xl border border-slate-200 bg-white p-2 shadow-card-hover">
          <p className="px-3 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">
            {t('nav.solutionsSub')}
          </p>
          <ul>
            {items.map((a) => (
              <li key={a.key}>
                <Link
                  to={a.href}
                  onClick={() => setOpen(false)}
                  aria-current={isCurrent(a.href) ? 'page' : undefined}
                  className={`flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-slate-50 ${
                    isCurrent(a.href) ? 'bg-slate-50' : ''
                  }`}
                >
                  <span
                    className={`mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ${a.accent}`}
                  >
                    <a.icon className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-bold text-slate-900">{t(a.labelKey)}</span>
                    <span className="block text-xs leading-snug text-slate-500">{t(a.descKey)}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SolutionsMenu;
